import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  useAlert,
} from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BANNER_PAGE_SIZE,
  BannerErrorState,
  BannerFilterBar,
  BannerTable,
  useBannerFilters,
  useBannerList,
  useDeleteBanner,
} from '@/features/banner-management';
import type { BannerListItemViewModel } from '@/shared/api/generated';
import { ConfirmDialog } from '@/shared/ui/molecules/ConfirmDialog';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export const Route = createFileRoute('/_authenticated/banners/')({
  component: BannerListPage,
});

/** Página de listagem de Banners. */
export function BannerListPage() {
  const { t } = useTranslation('banner');
  const { alert } = useAlert();
  const navigate = useNavigate();

  const { filters, updateFilters, resetFilters } = useBannerFilters();
  const { banners, totalCount, totalPages, currentPage, isLoading, isError, refetch } =
    useBannerList(filters);
  const deleteBanner = useDeleteBanner();

  const [deleting, setDeleting] = useState<BannerListItemViewModel | null>(null);

  const handleDeleteConfirm = () => {
    if (deleting?.id == null) return;
    deleteBanner.mutate(
      { id: deleting.id },
      {
        onSuccess: () => alert({ alertType: 'success', title: t('banner.toast.deleteSuccess') }),
        onError: () => alert({ alertType: 'warning', title: t('banner.toast.deleteError') }),
        onSettled: () => setDeleting(null),
      },
    );
  };

  const addButton = (
    <Button
      hierarchy="primary"
      size="lg"
      leadingIcon={PlusIcon}
      onClick={() => void navigate({ to: '/banners/create' })}
    >
      {t('banner.actions.add')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-mini">
      <PageHeader
        title={t('banner.title')}
        subtitle={t('banner.subtitle', { count: totalCount })}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('banner.breadcrumb.banners')}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={addButton}
      />

      {isError ? (
        <BannerErrorState message={t('banner.toast.loadError')} onRetry={() => void refetch()} />
      ) : (
        <>
          <BannerFilterBar
            defaultValue={filters.search}
            defaultStatus={filters.status}
            defaultCompanyId={filters.companyId}
            onSearch={({ search, status, companyId }) =>
              updateFilters({ search, status, companyId })
            }
            onClear={resetFilters}
          />

          <BannerTable
            data={banners}
            isLoading={isLoading}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={BANNER_PAGE_SIZE}
            onPageChange={page => updateFilters({ page })}
            emptyStateAction={addButton}
            onEdit={banner =>
              void navigate({
                to: '/banners/$bannerId',
                params: { bannerId: String(banner.id) },
              })
            }
            onDelete={setDeleting}
          />
        </>
      )}

      <ConfirmDialog
        open={deleting !== null}
        tone="danger"
        title={t('banner.delete.title')}
        description={t('banner.delete.body', { title: deleting?.title ?? '' })}
        confirmLabel={t('banner.delete.confirm')}
        cancelLabel={t('banner.delete.cancel')}
        closeAriaLabel={t('banner.delete.close')}
        isPending={deleteBanner.isPending}
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
